'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import { onAuthStateChanged, sendEmailVerification, type User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { getMembershipLabel } from '@/lib/membership';
import { agentLinkTier } from '@/lib/agent-link';
import { useToast } from '@/hooks/use-toast';
import { E } from '@/lib/editorial';
import { X } from 'lucide-react';

type BannerState = 'pending' | 'sending' | 'sent' | 'too_many' | 'confirmed';

const HIDDEN_KEY = 'nla.verifyBannerHidden';

/**
 * Shown to a signed-in member whose agent link promised a membership (e.g. Storebrand's links) that
 * waits for a verified address; see claimAgentLinkMembership. They can resend the link and, once
 * confirmed, collect the membership and welcome email at once instead of at their next sign-in.
 */
export function VerifyEmailBanner() {
  const pathname = usePathname();
  const { toast } = useToast();
  const [user, setUser] = React.useState<User | null>(null);
  const [tierLabel, setTierLabel] = React.useState<string | null>(null);
  const [state, setState] = React.useState<BannerState>('pending');
  const [isChecking, setIsChecking] = React.useState(false);
  const [isHidden, setIsHidden] = React.useState(false);

  /** Verified: refresh the token (the server reads email_verified from it) and collect what waits. */
  const collect = React.useCallback(async (current: User): Promise<boolean> => {
    const idToken = await current.getIdToken(true);
    const { claimAgentLinkMembership, sendWelcomeEmailForAgent } = await import('@/lib/actions');
    const [claim] = await Promise.all([
      claimAgentLinkMembership(idToken),
      sendWelcomeEmailForAgent(idToken).catch(error => console.error('[VerifyEmailBanner] Welcome email failed:', error)),
    ]);
    return claim.granted;
  }, []);

  React.useEffect(() => {
    try {
      setIsHidden(window.sessionStorage.getItem(HIDDEN_KEY) === '1');
    } catch {
      // Storage blocked: the banner just shows.
    }

    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (current) => {
      setUser(current);
      setTierLabel(null);
      setState('pending');
      if (!current) return;
      try {
        // A brand-new account's profile is written by AuthButton (createUserProfile) right after
        // sign-up, so it may not exist yet: wait for it a little.
        let profile: Record<string, any> | undefined;
        for (let attempt = 0; attempt < 8 && !cancelled; attempt++) {
          profile = (await getDoc(doc(db, 'users', current.uid))).data();
          if (profile || auth.currentUser?.uid !== current.uid) break;
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        if (cancelled || auth.currentUser?.uid !== current.uid) return;
        const tier = agentLinkTier(profile?.agentLinkTierPending);
        if (tier === 'free') return;
        // Verified on another device, or the token is older than the verification: catch up now.
        if (!current.emailVerified) await current.reload().catch(() => {});
        if (auth.currentUser?.emailVerified) {
          if (await collect(current)) setState('confirmed');
          else return;
        }
        setTierLabel(getMembershipLabel(tier));
      } catch (error) {
        console.error('[VerifyEmailBanner] Could not read the profile:', error);
      }
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [collect]);

  if (!user || !tierLabel || pathname.startsWith('/auth')) return null;
  if (isHidden && state !== 'confirmed') return null;

  const hide = () => {
    setIsHidden(true);
    if (state === 'confirmed') {
      setTierLabel(null);
      return;
    }
    try {
      window.sessionStorage.setItem(HIDDEN_KEY, '1');
    } catch {
      // Hidden for this page view only.
    }
  };

  const resend = async () => {
    setState('sending');
    try {
      await sendEmailVerification(user);
      setState('sent');
    } catch (error: any) {
      console.error('[VerifyEmailBanner] Resending verification failed:', error?.code);
      setState(error?.code === 'auth/too-many-requests' ? 'too_many' : 'pending');
      if (error?.code !== 'auth/too-many-requests') {
        toast({ title: 'Could not send', description: 'Please try again in a moment.', variant: 'destructive' });
      }
    }
  };

  const checkConfirmed = async () => {
    setIsChecking(true);
    try {
      await user.reload();
      if (!auth.currentUser?.emailVerified) {
        toast({ title: 'Not confirmed yet', description: 'Open the link in the email first, then try again.' });
        return;
      }
      if (await collect(user)) {
        setState('confirmed');
      } else {
        // Nothing to grant after all (already a member, or granted at sign-in meanwhile).
        toast({ title: 'Email confirmed', description: 'Thank you.' });
        setTierLabel(null);
      }
    } catch (error) {
      console.error('[VerifyEmailBanner] Checking verification failed:', error);
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.', variant: 'destructive' });
    } finally {
      setIsChecking(false);
    }
  };

  const confirmed = state === 'confirmed';
  const button: React.CSSProperties = {
    ...E.btn,
    height: 44,
    padding: '0 26px',
    borderRadius: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  };
  const label: React.CSSProperties = { ...E.label, fontSize: 10, marginBottom: 0 };

  return (
    <section
      aria-label="Email confirmation"
      aria-live="polite"
      style={{
        background: confirmed ? E.ink : E.cream2,
        color: confirmed ? E.cream : E.ink,
        borderBottom: `1px solid ${E.ruleSoft}`,
        fontFamily: E.sans,
      }}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8 px-5 md:px-14 py-[18px]">
        <div className="flex items-start gap-3 flex-grow min-w-0">
          <div className="flex flex-col gap-1 flex-grow min-w-0">
            {confirmed ? (
              <>
                <div style={{ ...label, color: E.gold }}>Welcome</div>
                <div style={{ fontFamily: E.serif, fontSize: 21, fontStyle: 'italic', lineHeight: 1.25 }}>
                  Your {tierLabel} membership is active.
                </div>
              </>
            ) : state === 'sent' ? (
              <>
                <div style={{ ...label, color: '#6e5a2f' }}>Link sent</div>
                <div style={{ fontFamily: E.serif, fontSize: 21, fontStyle: 'italic', lineHeight: 1.25 }}>
                  Check your inbox, and the spam folder.
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: '#55544f' }}>
                  Sent to {user.email}. Open the link, then come back here.
                </div>
              </>
            ) : state === 'too_many' ? (
              <>
                <div style={{ ...label, color: '#8a3b2a' }}>Could not send</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: E.inkSoft }}>
                  We have sent several links already. Wait a few minutes and try again, or contact us.
                </div>
              </>
            ) : (
              <>
                <div style={{ ...label, color: '#6e5a2f' }}>Membership pending</div>
                <div style={{ fontFamily: E.serif, fontSize: 21, fontStyle: 'italic', lineHeight: 1.25 }}>
                  Confirm your email to activate your {tierLabel} membership.
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: '#55544f' }}>
                  We sent a confirmation link to {user.email}. Your membership and welcome letter follow as soon as it is confirmed.
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={hide}
            aria-label={confirmed ? 'Close' : 'Hide for now'}
            className="md:hidden flex items-center justify-center shrink-0 -mt-2.5 -mr-3"
            style={{ width: 44, height: 44, border: 'none', background: 'transparent', color: confirmed ? E.cream : '#55544f', cursor: 'pointer' }}
          >
            <X size={16} strokeWidth={1.4} />
          </button>
        </div>

        <div className="flex items-center gap-5">
          {state === 'sent' && (
            <button
              type="button"
              onClick={checkConfirmed}
              disabled={isChecking}
              className="w-full md:w-auto"
              style={{ ...button, background: 'transparent', color: E.ink, border: `1px solid ${E.ink}` }}
            >
              {isChecking ? 'Checking…' : 'I have confirmed'}
            </button>
          )}
          {(state === 'pending' || state === 'sending' || state === 'too_many') && (
            <button
              type="button"
              onClick={resend}
              disabled={state === 'sending'}
              className="w-full md:w-auto"
              style={{ ...button, background: state === 'sending' ? '#55544f' : E.ink }}
            >
              {state === 'sending' ? 'Sending…' : state === 'too_many' ? 'Try again' : 'Resend link'}
            </button>
          )}
          <button
            type="button"
            onClick={hide}
            aria-label={confirmed ? 'Close' : 'Hide for now'}
            className="hidden md:flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, border: 'none', background: 'transparent', color: confirmed ? E.cream : '#55544f', cursor: 'pointer' }}
          >
            <X size={16} strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </section>
  );
}
